import axios from "axios";

// Define a baseURL baseada na variável de ambiente
// local: npm run dev:local -> http://localhost:3002/api
// hml: npm run dev:hml -> http://homologacao-api-node-trdmdia-env.eba-zxdzkthc.us-east-1.elasticbeanstalk.com/api
// prd: npm run build -> https://api.trendmidia.com.br/api
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Fallback para local se não houver variável de ambiente definida
  return "http://localhost:3002/api";
};

/**
 * @constant PUBLIC_ROUTES
 * @description Lista de rotas públicas que não requerem autenticação
 */
const PUBLIC_ROUTES = [
  '/login',
  '/auth/forgot-password',
  '/auth/validate-reset-token',
  '/auth/reset-password',
  '/auth/register'
];

/**
 * @function isPublicRoute
 * @description Verifica se uma URL é uma rota pública
 * @param {string} url - URL a ser verificada
 * @returns {boolean} - True se for uma rota pública
 */
const isPublicRoute = (url) => {
  if (!url) return false;
  return PUBLIC_ROUTES.some(route => url.includes(route));
};

export const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: 60000,
});

/**
 * @method handleApiError
 * @description Trata erros de requisições API
 * @param {Object} error - Erro capturado pelo middleware
 * @returns {Object} - Objeto de erro processado
 * @since 25/08/2025
 */
export const handleApiError = (error) => {
  // Tratar cancelamentos (AbortController / navegação / reload) sem ruído
  if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
    return {
      success: false,
      message: "canceled",
      code: "CANCELED",
      status: null,
      details: [],
    };
  }
  const hasResponse = !!(error && error.response);
  const status = hasResponse ? error.response.status : null;

  // Log somente erros não esperados (>=500) ou problemas de rede
  if (!hasResponse || (status && status >= 500)) {
    console.error("Erro capturado pelo middleware:", error);
  }

  if (hasResponse && error.response.data) {
    const apiError = error.response.data;
    const nested = apiError && typeof apiError.error === "object" ? apiError.error : null;
    const messageFromApi =
      (nested && (nested.message || nested.error)) || apiError.message || apiError.error;
    const codeFromApi = (nested && nested.code) || apiError.code || (status ? `HTTP_${status}` : "UNKNOWN_ERROR");
    const detailsFromApi = (nested && nested.details) || apiError.details || [];
    const fieldFromApi = (nested && nested.field) || apiError.field || null;
    return {
      success: false,
      message: messageFromApi || "Ocorreu um erro inesperado. Tente novamente.",
      code: codeFromApi,
      status: status || null,
      field: fieldFromApi,
      details: Array.isArray(detailsFromApi) ? detailsFromApi : [detailsFromApi].filter(Boolean),
    };
  }

  // Fallback para outros tipos de erro
  return {
    success: false,
    message: error?.message || "Ocorreu um erro inesperado. Tente novamente.",
    code: "NETWORK_ERROR",
    status: null,
    details: [],
  };
};

/**
 * @function setAuthToken
 * @description Define o token de autenticação no cabeçalho da requisição
 * @param {string} token - O token de autenticação
 * @since 27/08/2025 - v2.7.27
 */
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.Authorization;
  }
};

/**
 * @function clearAuthData
 * @description Limpa todos os dados de autenticação
 * @since 27/08/2025 - v2.7.27
 */
export const clearAuthData = () => {
  localStorage.removeItem("user_token");
  delete apiClient.defaults.headers.Authorization;
};

/**
 * @method createSession
 * @description Cria uma sessão
 * @param {Object} user - Usuário
 * @returns {Promise} - Promise com a resposta da sessão
 * @since 25/08/2025
 */
export const createSession = async (user) => {
  try {
    const response = await apiClient.post("/login", {
      login: user.login,
      password: user.password,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * @function apiClient.interceptors.request.use
 * @description Intercepta a requisição
 * @since 27/08/2025 - v2.7.27
 */
apiClient.interceptors.request.use(
  (config) => {
    try {
      // Não adiciona token de autorização para rotas públicas
      if (isPublicRoute(config.url)) {
        return config;
      }
      
      const user = localStorage.getItem("user_token");
      if (user) {
        const parsedUser = JSON.parse(user);
        if (parsedUser.token) {
          config.headers.Authorization = `Bearer ${parsedUser.token}`;
        }
      }
    } catch (error) {
      console.error("Erro ao processar token:", error);
      localStorage.removeItem("user_token");
    }
    return config;
  },
  (error) => {
    return Promise.reject(handleApiError(error));
  }
);

/**
 * @function apiClient.interceptors.response.use
 * @description Intercepta a resposta e trata o erro 401
 * @since 27/08/2025 - v2.7.27
 */
apiClient.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data)
    ) {
      response.data.success = true;
    }

    return response;
  },
  async (error) => {
    // Silenciar cancelamentos (AbortController / navegação / reload)
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      return Promise.reject({
        success: false,
        message: "canceled",
        code: "CANCELED",
        status: null,
        details: [],
      });
    }
    const originalRequest = error.config;

    // Verifica se é uma rota pública que não requer autenticação
    const publicRoute = isPublicRoute(originalRequest.url);
    
    // Para rotas públicas com erro 401, não tentamos renovar token
    if (error.response?.status === 401 && !originalRequest._retry && !publicRoute) {
      originalRequest._retry = true;

      try {
        const user = localStorage.getItem("user_token");
        
        // Só tenta renovar se houver um token salvo
        if (!user) {
          throw new Error("Sem token para renovar");
        }
        
        const parsedUser = JSON.parse(user);
        const response = await createSession(parsedUser);
        const userData = response.data;
        
        if (userData && userData.token) {
          setAuthToken(userData.token);
          localStorage.setItem("user_token", JSON.stringify(userData));
          return apiClient(originalRequest); // Tenta novamente a requisição
        }
      } catch (refreshError) {
        console.error("Erro ao renovar token:", refreshError);
        clearAuthData();
        // Não redireciona em rotas públicas (login, esqueci senha, redefinir senha)
        const isPublicAuthPage = typeof window !== 'undefined' && (
          window.location.pathname.includes('/login') ||
          window.location.pathname.includes('/forgot-password') ||
          window.location.pathname.includes('/reset-password')
        );
        if (typeof window !== 'undefined' && !isPublicAuthPage) {
          window.location.replace('/login');
        }
        return Promise.reject(handleApiError(refreshError));
      }
    }

    const processedError = handleApiError(error);
    // Evita log ruidoso para erros esperados (400/404/409/422)
    if (
      processedError.code !== "CANCELED" &&
      !(processedError.status && [400, 404, 409, 422].includes(processedError.status))
    ) {
      console.error("Erro processado:", processedError);
    }
    return Promise.reject(processedError);
  }
);

/**
 * @method createSuccessResponse
 * @description Cria uma resposta de sucesso
 * @param {Object} data - Dados da resposta
 * @param {string} message - Mensagem da resposta
 * @returns {Object} - Resposta de sucesso
 * @since 25/08/2025
 */
export const createSuccessResponse = (
  data,
  status = 200,
  message = "Operação realizada com sucesso"
) => {
  return {
    success: true,
    status,
    message,
    data,
  };
};

/**
 * @method createErrorResponse
 * @description Cria uma resposta de erro
 * @param {string} message - Mensagem da resposta
 * @param {number} status - Status da resposta
 * @param {Object} error - Erro da resposta
 * @param {string} code - Código do erro
 * @param {Array} details - Detalhes do erro
 * @returns {Object} - Resposta de erro
 * @since 25/08/2025
 */
export const createErrorResponse = (
  message,
  status = 500,
  error = null,
  code = null,
  details = []
) => {
  return {
    success: false,
    status,
    message,
    error,
    code: code || `HTTP_${status}`,
    details: details || [],
  };
};

/**
 * @method isApiError
 * @description Verifica se um erro é do formato padrão da API
 * @param {Object} error - Erro a ser verificado
 * @returns {boolean} - True se for um erro da API
 * @since 25/08/2025
 */
export const isApiError = (error) => {
  return (
    error &&
    typeof error === "object" &&
    (error.message || error.error) &&
    error.code &&
    typeof (error.message || error.error) === "string" &&
    typeof error.code === "string"
  );
};

/**
 * @method getErrorMessage
 * @description Extrai a mensagem de erro de um objeto de erro da API
 * @param {Object} error - Erro da API
 * @param {string} fallbackMessage - Mensagem padrão caso não seja possível extrair
 * @returns {string} - Mensagem de erro
 * @since 25/08/2025
 */
export const getErrorMessage = (
  error,
  fallbackMessage = "Ocorreu um erro inesperado"
) => {
  console.log(error?.message || error);
  if (isApiError(error)) {
    return error.message || error.error;
  }

  if (error && typeof error === "object") {
    // suporta formato aninhado vindo do middleware
    const nested = error.error && typeof error.error === "object" ? error.error : null;
    return (
      (nested && (nested.message || nested.error)) ||
      error.message ||
      error.msg ||
      error.error ||
      fallbackMessage
    );
  }

  if (typeof error === "string") {
    return error;
  }

  return fallbackMessage;
};

/**
 * @method getErrorCode
 * @description Extrai o código de erro de um objeto de erro da API
 * @param {Object} error - Erro da API
 * @param {string} fallbackCode - Código padrão caso não seja possível extrair
 * @returns {string} - Código do erro
 * @since 25/08/2025
 */
export const getErrorCode = (error, fallbackCode = "UNKNOWN_ERROR") => {
  if (isApiError(error)) {
    return error.code;
  }

  if (error && typeof error === "object") {
    return error.code || fallbackCode;
  }

  return fallbackCode;
};
